import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Users, GraduationCap, BookOpen, Archive, Library, CalendarClock, AlertCircle, HelpCircle, Check, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import { targetOptions, langDisplayName } from "@/lib/languages";
import { eduError } from "@/lib/eduErrors";
import EducationFAQ from "@/components/EducationFAQ";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];
const fmtDate = (d, locale) => (d ? new Date(d).toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" }) : "");

export default function TeacherDashboard() {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [classes, setClasses] = useState({ active: [], archived: [] });
  const [creating, setCreating] = useState(false);
  const [help, setHelp] = useState(false);

  const load = async () => {
    try {
      const [ov, cl] = await Promise.all([api.get("/teacher/overview"), api.get("/classes")]);
      setOverview(ov.data); setClasses(cl.data);
    } catch (e) { console.error("teacher load failed", e); }
  };
  useEffect(() => { load(); }, []);

  const orgs = overview?.organizations || [];
  const isTeacher = orgs.length > 0 || classes.active.length > 0;
  const isPlatformAdmin = !!user?.is_admin; // owner/platform_admin (server-computed); Resource Studio access only, no teacher role
  const firstRun = overview && orgs.length > 0 && classes.active.length === 0 && classes.archived.length === 0;
  const canCreate = orgs.some((o) => !o.pilot_expired);
  const expiredOrgs = orgs.filter((o) => o.pilot_expired);

  return (
    <AppLayout>
      <Seo title="Teacher Dashboard · FluentLoop" noindex />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="font-cabinet font-extrabold text-3xl flex items-center gap-2"><GraduationCap className="w-7 h-7" /> {t("teacher.dashboard")}</h1>
          {isTeacher && !firstRun && (
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => setHelp(true)} className="nb-btn-outline py-2 px-3 text-sm" data-testid="teacher-help-btn"><HelpCircle className="w-4 h-4" /> {t("edu.teach.howLink")}</button>
              <Link to="/teacher/library" className="nb-btn-primary py-2 px-4 text-sm" data-testid="mission-library-link"><Library className="w-4 h-4" /> {t("edu.teach.missionLibrary")}</Link>
              <Link to="/education/resources" className="nb-btn-outline py-2 px-4 text-sm" data-testid="resource-studio-link">{t("al.studio")}</Link>
              <Link to="/missions" className="nb-btn-outline py-2 px-4 text-sm" data-testid="my-missions-link">{t("lib.myMissions")}</Link>
              {canCreate && <button onClick={() => setCreating((v) => !v)} className="nb-btn-primary py-2 px-4 text-sm" data-testid="create-class-btn"><Plus className="w-4 h-4" /> {t("edu.teach.createBtn")}</button>}
            </div>
          )}
          {!isTeacher && isPlatformAdmin && (
            <Link to="/education/resources" className="nb-btn-outline py-2 px-4 text-sm" data-testid="resource-studio-admin-link">{t("al.studio")}</Link>
          )}
        </div>

        {overview && !isTeacher && (
          <div className="nb-card bg-butter p-6 mt-6" data-testid="teacher-not-member">{t("edu.teach.notTeacher")} <Link to="/education" className="underline font-bold">{t("edu.teach.apply")}</Link></div>
        )}

        {expiredOrgs.map((o) => (
          <div key={o.org_id} className="nb-card bg-peach p-4 mt-6 flex gap-3" data-testid={`teacher-pilot-expired-${o.org_id}`}>
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <div><div className="font-bold">{t("edu.cls.expiredH")} · <bdi>{o.name}</bdi></div><p className="text-sm mt-1">{t("edu.cls.expiredB")}</p></div>
          </div>
        ))}
        {firstRun ? (
          <FirstRun org={orgs.find((o) => !o.pilot_expired) || orgs[0]} canCreate={canCreate} t={t} locale={locale} onCreate={() => setCreating(true)} onHelp={() => setHelp(true)} />
        ) : isTeacher && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
              <Stat icon={BookOpen} bg="bg-mint" label={t("edu.teach.activeClasses")} value={overview?.active_classes ?? "—"} id="stat-classes" />
              <Stat icon={Users} bg="bg-sky" label={t("edu.teach.activeStudents")} value={overview?.total_active_students ?? "—"} id="stat-students" />
              <Stat icon={GraduationCap} bg="bg-lavender" label={t("edu.teach.schools")} value={orgs.length} id="stat-schools" />
            </div>
            {orgs.map((o) => <PilotRow key={o.org_id} org={o} t={t} locale={locale} onReload={load} />)}
          </>
        )}

        {creating && canCreate && <CreateClass orgs={orgs.filter((o) => !o.pilot_expired)} t={t} locale={locale} onDone={() => { setCreating(false); load(); }} onCancel={() => setCreating(false)} />}

        {isTeacher && !firstRun && <ClassList classes={classes} t={t} locale={locale} />}
      </div>
      <HowItWorks open={help} onOpenChange={setHelp} t={t} />
    </AppLayout>
  );
}

const Stat = ({ icon: Icon, bg, label, value, id }) => (
  <div className={`nb-card ${bg} p-5`} data-testid={id}><Icon className="w-6 h-6" /><div className="font-cabinet font-extrabold text-3xl mt-1">{value}</div><div className="font-bold text-sm">{label}</div></div>
);

function pilotText(org, t, locale) {
  const date = fmtDate(org.education_access_end, locale);
  if (org.pilot_active) return t("edu.teach.pilotActive", { date });
  if (org.education_access_end && org.education_status !== "suspended") return t("edu.teach.pilotEnded", { date });
  return t("edu.teach.pilotInactive");
}

function FirstRun({ org, canCreate, t, locale, onCreate, onHelp }) {
  return (
    <div className="nb-card bg-mint p-6 sm:p-8 mt-6" data-testid="teacher-first-run">
      <h2 className="font-cabinet font-extrabold text-2xl sm:text-3xl">{t("edu.teach.welcome")}</h2>
      <div className="flex flex-wrap gap-2 mt-3 text-sm">
        <span className="nb-tag bg-white" data-testid="first-run-school"><bdi>{org.name}</bdi></span>
        <span className="nb-tag bg-white" data-testid="first-run-role">{t(`edu.roles.${org.role === "school_admin" ? "school_admin" : "teacher"}`)}</span>
        <span className="nb-tag bg-white" data-testid="first-run-pilot">{pilotText(org, t, locale)}</span>
      </div>
      <p className="mt-4 max-w-xl">{canCreate ? t("edu.teach.next") : t("edu.cls.expiredB")}</p>
      <div className="flex flex-wrap gap-3 mt-5">
        {canCreate && <button onClick={onCreate} className="nb-btn-primary py-3 px-6" data-testid="first-class-btn"><Plus className="w-4 h-4" /> {t("edu.teach.createFirst")}</button>}
        <button onClick={onHelp} className="nb-btn-outline bg-white py-3 px-5" data-testid="first-run-help-btn"><HelpCircle className="w-4 h-4" /> {t("edu.teach.howLink")}</button>
      </div>
    </div>
  );
}

function HowItWorks({ open, onOpenChange, t }) {
  const list = (k, Icon) => (
    <ul className="space-y-1 mt-1">{t(`edu.teach.${k}`).split("|").map((x) => <li key={x} className="flex gap-2 text-sm"><Icon className="w-4 h-4 shrink-0 mt-0.5" />{x}</li>)}</ul>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="teacher-help-panel">
        <DialogHeader><DialogTitle className="font-cabinet pe-8">{t("edu.teach.howLink")}</DialogTitle></DialogHeader>
        <ol className="space-y-2">
          {[1, 2, 3, 4].map((n) => (
            <li key={n} className="flex gap-3"><span className="w-7 h-7 rounded-full bg-mint border-2 border-ink flex items-center justify-center font-bold text-sm shrink-0">{n}</span>
              <div><div className="font-bold text-sm">{t(`edu.teach.s${n}H`)}</div><div className="text-sm text-gray-600">{t(`edu.teach.s${n}B`)}</div></div></li>
          ))}
        </ol>
        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          <div className="nb-card bg-mint p-3" data-testid="help-can-see"><div className="font-bold text-sm">{t("edu.teach.canSeeH")}</div>{list("canSee", Check)}</div>
          <div className="nb-card bg-peach p-3" data-testid="help-cannot-see"><div className="font-bold text-sm">{t("edu.teach.cantSeeH")}</div>{list("cantSee", X)}</div>
        </div>
        <Link to="/education" className="text-sm underline mt-1" data-testid="help-learn-more">{t("edu.teach.learnMore")}</Link>
        <details className="mt-1" data-testid="teacher-faq">
          <summary className="text-sm font-bold cursor-pointer">{t("edu.faq.title")}</summary>
          <div className="mt-3"><EducationFAQ groups={["schools", "privacy", "pilot"]} testid="teacher-faq-list" /></div>
        </details>
      </DialogContent>
    </Dialog>
  );
}

function CreateClass({ orgs, t, locale, onDone, onCancel }) {
  const [form, setForm] = useState({ org_id: orgs[0]?.org_id || "", name: "", target_language: "es", level: "A1", description: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.org_id) { toast.error(t("edu.teach.needOrg")); return; }
    if (!form.name.trim()) { toast.error(t("edu.teach.needName")); return; }
    setBusy(true);
    try { await api.post("/classes", form); toast.success(t("edu.teach.created")); onDone(); }
    catch (err) { toast.error(eduError(err, t, t("edu.teach.failed"))); }
    finally { setBusy(false); }
  };
  const lbl = "text-xs font-bold uppercase tracking-widest rtl:tracking-normal text-gray-500";
  return (
    <form onSubmit={submit} className="nb-card p-6 mt-6 space-y-3" data-testid="create-class-form">
      <h3 className="font-cabinet font-bold text-lg">{t("edu.teach.newClass")}</h3>
      {orgs.length > 1 && (
        <select className="nb-input" value={form.org_id} onChange={(e) => set("org_id", e.target.value)} data-testid="class-org-select">
          {orgs.map((o) => <option key={o.org_id} value={o.org_id}>{o.name}</option>)}
        </select>
      )}
      <label className="block"><span className={lbl}>{t("edu.teach.name")}</span>
        <input className="nb-input mt-1" dir="auto" placeholder={t("edu.teach.namePh")} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="class-name-input" /></label>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block"><span className={lbl}>{t("edu.teach.lang")}</span>
          <select className="nb-input mt-1" value={form.target_language} onChange={(e) => set("target_language", e.target.value)} data-testid="class-lang-select">
            {targetOptions().map((l) => <option key={l.code} value={l.code}>{langDisplayName(l.code, locale)}</option>)}
          </select></label>
        <label className="block"><span className={lbl}>{t("edu.teach.level")}</span>
          <select className="nb-input mt-1" value={form.level} onChange={(e) => set("level", e.target.value)} data-testid="class-level-select">
            {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select></label>
      </div>
      <label className="block"><span className={lbl}>{t("edu.teach.desc")}</span>
        <textarea className="nb-input mt-1" dir="auto" rows="2" maxLength={1000} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="class-desc-input" /></label>
      <div className="flex gap-2">
        <button disabled={busy} className="nb-btn-primary flex-1 py-2" data-testid="submit-class-btn">{t("edu.teach.createBtn")}</button>
        <button type="button" onClick={onCancel} className="nb-btn-outline py-2 px-4" data-testid="cancel-class-btn">{t("edu.teach.cancel")}</button>
      </div>
    </form>
  );
}

function ClassList({ classes, t, locale }) {
  return (
    <>
      <h2 className="font-cabinet font-extrabold text-2xl mt-10">{t("teacher.myClasses")}</h2>
      <div className="grid sm:grid-cols-2 gap-4 mt-4" data-testid="classes-list">
        {classes.active.length === 0 && <p className="text-gray-500 text-sm">{t("edu.teach.noClasses")}</p>}
        {classes.active.map((c) => (
          <Link key={c.class_id} to={`/class/${c.class_id}`} className="nb-card p-5 hover:-translate-y-0.5 transition-transform" data-testid={`class-card-${c.class_id}`}>
            <div className="font-cabinet font-bold text-lg"><bdi>{c.name}</bdi></div>
            <div className="text-sm text-gray-600">{langDisplayName(c.target_language, locale)} · {c.level} · <span data-testid={`class-students-${c.class_id}`}>{t("edu.teach.students", { count: c.student_count })}</span></div>
            <div className="nb-tag bg-mint text-ink mt-2 inline-block" dir="ltr" data-testid={`class-code-${c.class_id}`}>{t("edu.teach.code")} {c.class_code}</div>
          </Link>
        ))}
      </div>
      {classes.archived.length > 0 && (
        <>
          <h3 className="font-cabinet font-bold text-lg mt-8 flex items-center gap-2"><Archive className="w-5 h-5" /> {t("edu.teach.archived")}</h3>
          <div className="grid sm:grid-cols-2 gap-4 mt-3">
            {classes.archived.map((c) => (
              <Link key={c.class_id} to={`/class/${c.class_id}`} className="nb-card p-4 opacity-70" data-testid={`archived-class-${c.class_id}`}>
                <div className="font-bold"><bdi>{c.name}</bdi></div><div className="text-xs text-gray-500">{t("edu.teach.archived")}</div>
              </Link>
            ))}
          </div>
        </>
      )}
    </>
  );
}

function PilotRow({ org, t, locale, onReload }) {
  const [busy, setBusy] = useState(false);
  const d = org.days_remaining;
  const ended = !org.pilot_active;
  const near = org.pilot_active && typeof d === "number" && d <= 14;
  const Icon = ended ? AlertCircle : CalendarClock;
  const tone = ended ? "text-coral font-semibold" : near ? "text-amber-700" : "text-gray-500";

  const requestContinuation = async () => {
    setBusy(true);
    try { await api.post(`/orgs/${org.org_id}/continuation-request`, {}); toast.success(t("edu.teach.continuationSent")); onReload(); }
    catch (e) { toast.error(eduError(e, t, t("edu.teach.failed"))); }
    finally { setBusy(false); }
  };
  const canRequest = org.is_school_admin && !org.continuation_pending && (ended || (typeof d === "number" && d <= 45));

  return (
    <div className="flex items-center justify-between gap-3 mt-3 flex-wrap" data-testid="teacher-org-status">
      <div className={`text-sm flex items-center gap-1.5 ${tone}`}>
        <Icon className="w-4 h-4 shrink-0" />
        <span><b className="text-gray-700"><bdi>{org.name}</bdi></b> · <span data-testid={`pilot-line-${org.org_id}`}>{pilotText(org, t, locale)}</span></span>
      </div>
      <div>
        {org.continuation_pending && <span className="nb-tag bg-sky text-ink text-xs" data-testid="continuation-pending">{t("edu.teach.continuationPending")}</span>}
        {canRequest && (
          <button onClick={requestContinuation} disabled={busy} className="nb-btn-outline py-1.5 px-3 text-xs" data-testid={`request-continuation-${org.org_id}`}>
            {t("edu.teach.requestContinuation")}
          </button>
        )}
      </div>
    </div>
  );
}
